import { useDisclosure } from '@mantine/hooks'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useVideosDetail } from '../../api/hooks/Videos/useVideosDetail'
import ResultContent, {
  VideoDataProps,
} from '../../components/admin/ResultContent'
import BaseInput from '../../components/common/BaseInput'
import Label from '../../components/common/Label'
import SaveButton from '../../components/common/SaveButton'
import SaveResultModal from '../../components/common/SaveResultModal'
import AdminContentLayout from '../../components/layout/AdminContentLayout'
import AdminLayout from '../../components/layout/AdminLayout'
import { useLectureVideoData } from '../../hooks/useLectureVideoData'
import styles from '../../styles/admin/StudyContent.module.scss'
import { CurriculumProps } from '../../types/table'

const LectureEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const videoId = location.state?.videoId
  const [opened, { open, close }] = useDisclosure(false)
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error'
    message?: string
  } | null>(null)

  const fromPage = location.state?.fromPage || 1
  const level = location.state?.level || 1

  const { data } = useVideosDetail({ QUESTION_ID: videoId })
  const { curriculum, isLoading, handleSaveLecture } = useLectureVideoData(
    data?.data
  )

  const [videoInfo, setVideoInfo] = useState({
    title: '',
    vimeoId: '',
    hashKey: '',
  })

  useEffect(() => {
    if (data?.data) {
      setVideoInfo({
        title: data.data.TITLE || '',
        vimeoId: data.data.VIMEO_ID || '',
        hashKey: data.data.HASHKEY || '',
      })
    }
  }, [data])

  const handleSaveWithModal = useCallback(async () => {
    try {
      await handleSaveLecture(videoInfo)
      setSaveStatus({ type: 'success', message: 'Successfully saved.' })
      open()
      setTimeout(() => {
        navigate('/lectures', { state: { fromPage, level } })
      }, 2000)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Failed to save. Please try again.'
      setSaveStatus({ type: 'error', message: errorMessage })
      open()
    }
  }, [handleSaveLecture, videoInfo, open, navigate])

  const handleModalClose = useCallback(() => {
    close()
    setSaveStatus(null)
  }, [close])

  const handleModalConfirm = useCallback(() => {
    if (saveStatus?.type === 'success') {
      navigate('/lectures', { state: { fromPage, level } })
    } else {
      handleModalClose()
    }
  }, [saveStatus, navigate, handleModalClose])

  const curriculumData: CurriculumProps[] = [
    {
      level: <span>{data?.data?.LEVEL?.toString() || ''}</span>,
      mainChapter: <span>{data?.data?.MAIN?.toString() || ''}</span>,
      subChapter: <span>{data?.data?.SUB?.toString() || ''}</span>,
      unit: <span>{data?.data?.UNIT?.toString() || ''}</span>,
      detailUnit: <span>{data?.data?.DETAIL_UNIT?.toString() || ''}</span>,
      contentType: <Label text={data?.data?.TYPE?.toString() || 'Class'} />,
      questionLabel: (
        <span className={styles.questionLabelDisplay}>
          {curriculum.questionLabel || data?.data?.DEPTH?.toString() || ''}
        </span>
      ),
    },
  ]

  const videoData: VideoDataProps[] = [
    {
      videoTitle: (
        <BaseInput
          value={videoInfo.title}
          placeholder="video Title"
          onChange={(e) =>
            setVideoInfo((prev) => ({ ...prev, title: e.target.value }))
          }
          className={styles.videoTitleInput}
        />
      ),
      videoID: (
        <BaseInput
          value={videoInfo.vimeoId}
          placeholder="Vimeo ID"
          onChange={(e) =>
            setVideoInfo((prev) => ({ ...prev, vimeoId: e.target.value }))
          }
          className={styles.videoTitleInput}
        />
      ),
      hash: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <BaseInput
            value={videoInfo.hashKey}
            placeholder="Hash Key"
            onChange={(e) =>
              setVideoInfo((prev) => ({ ...prev, hashKey: e.target.value }))
            }
            disabled={isLoading}
            className={styles.videoTitleInput}
          />
        </div>
      ),
      vimeoId: videoInfo.vimeoId,
      hashKey: videoInfo.hashKey,
      title: videoInfo.title,
    },
  ]

  const saveButton = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: '20px',
      }}
    >
      <SaveButton onClick={handleSaveWithModal} disabled={isLoading}>
        {isLoading ? 'Saving...' : 'Save'}
      </SaveButton>
    </div>
  )

  return (
    <AdminLayout>
      <AdminContentLayout>
        <ResultContent
          curriculum={curriculumData}
          videoData={videoData}
          showPreview={true}
          saveButton={saveButton}
        />
      </AdminContentLayout>
      <SaveResultModal
        opened={opened}
        onClose={handleModalClose}
        status={saveStatus}
        onConfirm={handleModalConfirm}
      />
    </AdminLayout>
  )
}

export default LectureEdit
